/**
 * A numeric item with several parts, answered one part at a time (pedagogy spec §4.3.4, B4).
 * Part (a) is active: a value, a rating, then "Rätta deluppgift (a)". A checked part locks and shows
 * right or wrong with the near-miss kind, never a value; focus moves to the next input and a
 * polite status line announces the result. Later parts are locked rows ("Låses upp när du har
 * svarat på deluppgift (a)") whose prompt is not shown. The page owns the state and the requests:
 * this component only draws the rows and forwards the two actions.
 */
import clsx from "clsx";
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useId, useImperativeHandle, useRef, type Ref } from "react";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import type { Figure as FigureData, Localized as LocalizedValue } from "@shared/types";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { NumericInput } from "@client/components/NumericInput";
import { Button } from "@client/components/ui";
import { useT } from "@client/i18n";
import { ConfidenceControl, type ConfidenceControlHandle } from "./ConfidenceControl";
import { nearMissText } from "./DiagnosisMessage";
import type { PartCheck } from "./session";
import { checkedCount, dependsOnWrong } from "./segmented";

export interface SegmentedPart {
  id: string;
  unit: string;
  prompt?: LocalizedValue | undefined;
}

export interface SegmentedHandle {
  /** Moves focus to the active part's input (a refused check names it). */
  focusActive(): void;
}

export interface SegmentedNumericProps {
  parts: readonly SegmentedPart[];
  /** The question's figures; those tagged with a part id are drawn inside that part's card. */
  figures?: readonly FigureData[] | undefined;
  activeRef?: Ref<SegmentedHandle> | undefined;
  /** The parts checked so far, by part id. */
  checks: Readonly<Record<string, PartCheck>>;
  /** Draft values; only the active part's is read. */
  values: Readonly<Record<string, string>>;
  onValueChange: (partId: string, value: string) => void;
  /** The active part's rating. */
  confidence: ConfidenceLevel | undefined;
  onConfidenceChange: (level: ConfidenceLevel) => void;
  confidenceError?: string | undefined;
  confidenceRef?: Ref<ConfidenceControlHandle> | undefined;
  /** Inline error when the active part was checked without a readable number. */
  valueError?: string | undefined;
  /** The result of the latest check, announced politely ("Deluppgift (a): rätt."). */
  announcement: string;
  /** The step's primary action: check the active part, or send the answer once all are checked. */
  onPrimary: () => void;
  /** Send what is checked now; unchecked parts grade as empty. */
  onCheckRest: () => void;
  onRevealHint?: (() => void) | undefined;
  /** A `/check-part` request is in flight. */
  checking?: boolean | undefined;
  /** The final `/answer` is in flight. */
  submitting?: boolean | undefined;
}

export function SegmentedNumeric({
  parts,
  figures,
  activeRef,
  checks,
  values,
  onValueChange,
  confidence,
  onConfidenceChange,
  confidenceError,
  confidenceRef,
  valueError,
  announcement,
  onPrimary,
  onCheckRest,
  onRevealHint,
  checking,
  submitting,
}: SegmentedNumericProps) {
  const t = useT();
  const base = useId();
  // P1-8: the active part's box names the keyboard path to its rating, as in AnswerArea.
  const keysHintId = `${base}-keys`;
  const active = parts.find((part) => checks[part.id] === undefined);
  const activeId = active?.id;
  const done = checkedCount(parts, checks);

  useImperativeHandle(
    activeRef,
    () => ({
      focusActive() {
        if (activeId !== undefined) document.getElementById(`${base}-${activeId}`)?.focus();
      },
    }),
    [activeId, base],
  );

  // After a check the next input takes focus (a first mount must not steal it).
  const previousActive = useRef(activeId);
  useEffect(() => {
    if (
      previousActive.current !== undefined &&
      activeId !== undefined &&
      previousActive.current !== activeId
    ) {
      document.getElementById(`${base}-${activeId}`)?.focus();
    }
    previousActive.current = activeId;
  }, [activeId, base]);

  return (
    <div className="space-y-4">
      <p role="status" className="min-h-5 text-sm font-medium text-ink-800 dark:text-ink-100">
        {announcement}
      </p>
      {parts.map((part, index) => {
        const check = checks[part.id];
        const isActive = part.id === activeId;
        const previous = parts[index - 1];
        const chained = check ? undefined : dependsOnWrong(parts, checks, part.id);
        return (
          <div
            key={part.id}
            role="group"
            aria-labelledby={`${base}-heading-${part.id}`}
            className={clsx(
              "rounded-lg border p-3",
              isActive
                ? "border-accent-300 dark:border-accent-700"
                : "border-ink-200 dark:border-ink-800",
            )}
          >
            <h3
              id={`${base}-heading-${part.id}`}
              className="mb-1 text-sm font-semibold text-ink-900 dark:text-ink-50"
            >
              {t("practice.part.heading", { part: part.id })}
            </h3>
            {check || isActive ? (
              <>
                {part.prompt && (
                  <div className="mb-1 text-sm text-ink-600 dark:text-ink-400">
                    <Localized value={part.prompt} variant="compact" />
                  </div>
                )}
                {(figures ?? [])
                  .filter((figure) => figure.partId === part.id)
                  .map((figure, i) => (
                    <Figure key={i} figure={figure} />
                  ))}
                <NumericInput
                  id={`${base}-${part.id}`}
                  value={check ? check.value : (values[part.id] ?? "")}
                  onChange={(value) => onValueChange(part.id, value)}
                  unit={part.unit}
                  label={part.id.toUpperCase()}
                  disabled={check !== undefined}
                  onSubmit={onPrimary}
                  describedBy={isActive ? keysHintId : undefined}
                />
              </>
            ) : (
              // A locked part is text: its prompt is not rendered until it is the active one.
              <p className="text-sm text-ink-600 dark:text-ink-400">
                {t("practice.part.locked", { previous: previous?.id ?? "" })}
              </p>
            )}
            {check && (
              <p
                className={clsx(
                  "mt-1 flex flex-wrap items-center gap-2 text-sm",
                  check.outcome === "right"
                    ? "text-green-700 dark:text-green-300"
                    : "text-red-700 dark:text-red-300",
                )}
              >
                {check.outcome === "right" ? (
                  <CheckCircle2 size={14} aria-hidden="true" className="shrink-0" />
                ) : (
                  <XCircle size={14} aria-hidden="true" className="shrink-0" />
                )}
                <span className="sr-only">
                  {check.outcome === "right"
                    ? t("practice.feedback.partCorrect")
                    : t("practice.feedback.partIncorrect")}
                </span>
                {check.outcome === "wrong" && check.nearMissKind && (
                  <span className="text-amber-700 dark:text-amber-300">
                    {nearMissText(t, { kind: check.nearMissKind }, part.unit)}
                  </span>
                )}
              </p>
            )}
            {chained && (
              <p className="mt-2 text-sm text-ink-600 dark:text-ink-400">
                {t("practice.part.dependsOnWrong", { part: part.id, previous: chained })}
              </p>
            )}
            {isActive && (
              <div className="mt-3 space-y-3">
                <ConfidenceControl
                  ref={confidenceRef}
                  legend={t("practice.part.confidence", { part: part.id })}
                  value={confidence}
                  onChange={onConfidenceChange}
                  error={confidenceError}
                  onSubmit={onPrimary}
                  onRevealHint={onRevealHint}
                />
                <p
                  id={keysHintId}
                  className="-mt-2 text-xs text-ink-500 dark:text-ink-400 max-sm:sr-only"
                >
                  {t("practice.confidence.keysHint")}
                </p>
                {valueError && (
                  <p role="alert" className="text-xs text-red-700 dark:text-red-300">
                    {valueError}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button className="max-sm:min-h-11" onClick={onPrimary} loading={checking}>
                    {t("practice.part.check", { part: part.id })}
                  </Button>
                  {done > 0 && (
                    <Button
                      className="max-sm:min-h-11"
                      variant="secondary"
                      onClick={onCheckRest}
                      loading={submitting}
                    >
                      {t("practice.part.checkRest")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {activeId === undefined && (
        // Every part is checked but the answer did not go out (a failed request): send it again.
        <Button className="max-sm:min-h-11" onClick={onPrimary} loading={submitting}>
          {t("practice.submit")}
        </Button>
      )}
    </div>
  );
}
